'use client';

import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, RefreshCw, Check, FileText, Type, Hash, List, Clock, Hash as HashIcon } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { characterCounterContent } from '../../data/character-counter-content';
import Link from 'next/link';

type CountResult = {
    characters: number;
    charactersNoSpaces: number;
    words: number;
    lines: number;
    readingTime: number;
};

export default function CharacterCounter() {
    const [text, setText] = useState('');
    const [countResult, setCountResult] = useState<CountResult>({
        characters: 0,
        charactersNoSpaces: 0,
        words: 0,
        lines: 0,
        readingTime: 0
    });
    const [copied, setCopied] = useState(false);
    const [includeWhitespace, setIncludeWhitespace] = useState(true);
    const [wordsPerMinute, setWordsPerMinute] = useState(200);
    const { toast } = useToast();

    const calculateCounts = useCallback((text: string) => {
        const characters = text.length;
        const charactersNoSpaces = text.replace(/\s+/g, '').length;
        const words = text.trim() === '' ? 0 : text.trim().split(/\s+/).length;
        const lines = text === '' ? 0 : text.split('\n').length;
        const readingTime = Math.ceil(words / wordsPerMinute);

        return {
            characters,
            charactersNoSpaces,
            words,
            lines,
            readingTime: readingTime < 1 ? 1 : readingTime // At least 1 minute
        };
    }, [wordsPerMinute]);

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newText = e.target.value;
        setText(newText);
        setCountResult(calculateCounts(newText));
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        toast({
            title: 'Copied!',
            description: 'Text has been copied to clipboard.',
        });
        setTimeout(() => setCopied(false), 2000);
    };

    const clearText = () => {
        setText('');
        setCountResult({
            characters: 0,
            charactersNoSpaces: 0,
            words: 0,
            lines: 0,
            readingTime: 0
        });
    };

    const readingTimeLabel = countResult.readingTime === 1 ? 'minute' : 'minutes';
    const characterCount = includeWhitespace ? countResult.characters : countResult.charactersNoSpaces;

    return (
        <div className="container mx-auto px-4 py-8">
            <ToolPageIntro title="Character Counter">
                Count characters, words, lines, and reading time in any text. Toggle whether to include spaces, set your reading speed, and see stats update as you type. Useful for social posts, essays, and copy limits.
            </ToolPageIntro>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Text Input</CardTitle>
                            <CardDescription>Type or paste your text below to analyze it</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="relative">
                                <Textarea
                                    value={text}
                                    onChange={handleTextChange}
                                    placeholder="Type or paste your text here..."
                                    className="min-h-[300px] w-full font-mono"
                                    rows={10}
                                />
                                <div className="absolute right-3 top-3 flex space-x-2">
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={copyToClipboard}
                                        disabled={!text}
                                        className="h-9 w-9 bg-background/80 backdrop-blur-sm"
                                        aria-label="Copy to clipboard"
                                    >
                                        {copied ? (
                                            <Check className="h-4 w-4" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={clearText}
                                        disabled={!text}
                                        className="h-9 w-9 bg-background/80 backdrop-blur-sm"
                                        aria-label="Clear text"
                                    >
                                        <RefreshCw className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                    <Switch
                                        id="whitespace"
                                        checked={includeWhitespace}
                                        onCheckedChange={setIncludeWhitespace}
                                    />
                                    <Label htmlFor="whitespace">Include whitespace</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Label htmlFor="wpm">Words per minute:</Label>
                                    <select
                                        id="wpm"
                                        value={wordsPerMinute}
                                        onChange={(e) => setWordsPerMinute(Number(e.target.value))}
                                        className="bg-background border rounded px-2 py-1 text-sm"
                                    >
                                        <option value="150">Slow (150)</option>
                                        <option value="200">Average (200)</option>
                                        <option value="250">Fast (250)</option>
                                    </select>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Count Results</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <StatCard
                                    icon={<Type className="h-5 w-5 text-muted-foreground" />}
                                    label="Characters"
                                    value={characterCount.toLocaleString()}
                                    description={includeWhitespace ? 'Including spaces' : 'Without spaces'}
                                />
                                <StatCard
                                    icon={<HashIcon className="h-5 w-5 text-muted-foreground" />}
                                    label="Words"
                                    value={countResult.words.toLocaleString()}
                                    description="Word count"
                                />
                                <StatCard
                                    icon={<List className="h-5 w-5 text-muted-foreground" />}
                                    label="Lines"
                                    value={countResult.lines.toLocaleString()}
                                    description="Line count"
                                />
                                <StatCard
                                    icon={<Clock className="h-5 w-5 text-muted-foreground" />}
                                    label="Reading Time"
                                    value={`~${countResult.readingTime} ${readingTimeLabel}`}
                                    description={`at ${wordsPerMinute} wpm`}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>About Character Counter</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4 text-sm text-muted-foreground">
                            <p>
                                This tool helps you analyze your text by counting characters, words, lines, and estimating reading time.
                            </p>
                            <div className="space-y-2">
                                <h3 className="font-medium text-foreground">How it works:</h3>
                                <ul className="list-disc pl-5 space-y-1">
                                    <li>Characters: Counts each character in the text</li>
                                    <li>Words: Counts sequences of non-whitespace characters</li>
                                    <li>Lines: Counts the number of line breaks</li>
                                    <li>Reading Time: Estimates based on average reading speed</li>
                                </ul>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <div className="mt-12">
                <InformationalContent
                    title={characterCounterContent.title}
                    description={characterCounterContent.description}
                    content={characterCounterContent.content}
                    faqs={characterCounterContent.faqs}
                    relatedTools={characterCounterContent.relatedTools}
                />
            </div>
        </div>
    );
}

function StatCard({ icon, label, value, description }: { icon: React.ReactNode; label: string; value: string; description: string }) {
    return (
        <div className="border rounded-lg p-4">
            <div className="flex items-center space-x-2">
                <div className="p-1 rounded-md bg-muted">
                    {icon}
                </div>
                <span className="text-sm font-medium text-muted-foreground">{label}</span>
            </div>
            <p className="mt-2 text-2xl font-bold">{value}</p>
            <p className="text-xs text-muted-foreground">{description}</p>
        </div>
    );
}
